import { gql, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Badge,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Card,
  CardFooter,
  Link,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';

import { useIsAppInBackgroundContext } from 'src/app/appLayout/hooks/useIsAppInBackground';
import { useGraphRef } from 'src/app/graph/hooks/useGraphRef';
import { OperationIdBadge } from 'src/app/graph/operationsPage/OperationIdBadge';
import { insightsRouteConfig } from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { useOperationInsightsFilter } from 'src/hooks/useInsightsFilter';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { date } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import {
  ErrorInsightsListGroupByColumn,
  ErrorInsightsOpDrilldownQuery,
  ErrorInsightsOpItemFragment,
} from 'src/lib/graphqlTypes/types';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { notEmpty } from 'src/lib/ts/notEmpty';
import {
  isOperationUnexecutable,
  unexecutableOperationNameForId,
  unexecutableOperationReason,
} from 'src/lib/unexecutableOperationReason';

import { buildErrorFiltersForListQuery } from './buildErrorFilters';
import { ErrorBreakdownLoading } from './ErrorBreakdownLoading';
import { TraceRefBadge, errorTraceRefFragment } from './TraceRefBadge';

const errorInsightsOpItemFragment = gql`
  fragment ErrorInsightsOpItemFragment on ErrorInsightsListItem {
    errorCount
    operationId
    operationName
    traceRefs(limit: $traceRefsLimit) {
      totalCount
      items {
        ...ErrorTraceRefFragment
      }
    }
  }
`;

export const errorInsightsOpDrilldownQuery = gql`
  query ErrorInsightsOpDrilldownQuery(
    $graphId: ID!
    $variant: String!
    $from: Timestamp!
    $to: Timestamp!
    $first: Int!
    $filter: ErrorInsightsListFilterInput
    $groupBy: [ErrorInsightsListGroupByColumn!]
    $traceRefsLimit: Int!
  ) {
    graph(id: $graphId) {
      id
      variant(name: $variant) {
        id
        errorInsightsList(
          first: $first
          from: $from
          to: $to
          filter: $filter
          groupBy: $groupBy
        ) {
          edges {
            node {
              ...ErrorInsightsOpItemFragment
            }
            cursor
          }
          pageInfo {
            endCursor
            startCursor
          }
          totalCount
        }
      }
    }
  }
  ${errorInsightsOpItemFragment}
  ${errorTraceRefFragment}
`;
export interface OperationsDrilldownItemData {
  operationId: string | null;
  operationName: string | null;
  errorCount: number;
  traceRefs: ErrorInsightsOpItemFragment['traceRefs'];
}

interface Props {
  data: OperationsDrilldownItemData[];
}

function Operation({ id, name }: { id: string | null; name: string | null }) {
  const isMissingData = !id || !name;
  const displayName = name || unexecutableOperationNameForId(id) || 'unknown';
  const location = useLocation();
  return (
    <div className="flex items-center">
      <WrapMaybe
        on={!!id}
        element={
          <Link
            className="truncate"
            to={insightsRouteConfig.locationFrom({
              location,
              patch: {
                query: id ?? '',
                queryName: name,
                // It's not entirely clear that these should link
                // anywhere, but if they do, it would be nicer to
                // preserve the original filters if they were set.
                // As a first implementation, just clearing the filters
                // is better than leaving them in a weird drilldown state.
                errorsDrilldown: RouteConfig.DELETE,
                errorsServiceFilter: RouteConfig.DELETE,
                errorsCodeFilter: RouteConfig.DELETE,
                errorsPathFilter: RouteConfig.DELETE,
              },
            })}
          />
        }
      >
        {name && isOperationUnexecutable(name) ? (
          <>
            <span title={name} className="truncate italic">
              {name}
            </span>
            <Tooltip label={unexecutableOperationReason(name)}>
              <IconInfo className="ml-2" />
            </Tooltip>
          </>
        ) : (
          <span
            title={displayName}
            className={classnames('truncate', { italic: !name })}
          >
            {displayName}
          </span>
        )}
      </WrapMaybe>
      {id && (
        <OperationIdBadge
          className="ml-1.5 mr-1 hover:cursor-pointer"
          id={id}
        />
      )}
      {isMissingData && (
        <Tooltip label="Errors were reported, but the operation details were not captured by usage reporting.">
          <IconInfo className="ml-2" />
        </Tooltip>
      )}
    </div>
  );
}

export function OperationsDrilldownView({ data }: Props) {
  return (
    <TableContainer className="rounded border border-primary pt-1">
      <Table layout="fixed">
        <Thead>
          <Tr>
            <Th>Operation</Th>
            <Th isNumeric className="w-[10%] whitespace-nowrap">
              Errors
            </Th>
            <Th className="w-1/2">Recent traces</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.map((d) => (
            <Tr key={`${d.operationId}.${d.operationName}`}>
              <Td className="overflow-hidden">
                <Operation id={d.operationId} name={d.operationName} />
              </Td>
              <Td isNumeric>
                <span className="whitespace-nowrap font-mono">
                  {format.number(d.errorCount, '0[.]0a')}
                </span>
              </Td>
              <Td>
                {(d.traceRefs?.items ?? []).length > 0 && (
                  <span className="flex flex-wrap gap-2">
                    {d.traceRefs?.items?.map((traceRef) => (
                      <TraceRefBadge
                        key={traceRef.traceId}
                        traceRef={traceRef}
                      />
                    ))}
                  </span>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}

interface WithDataProps {
  pageSize?: number;
}

const defaultPageSize = 25;
const defaultTraceRefsLimit = 3;

export function OperationsDrilldown({
  pageSize = defaultPageSize,
}: WithDataProps) {
  const trackCustomEvent = useTrackCustomEvent();
  const { routeParams } = useOperationInsightsFilter();
  const timeRange = routeParams.range ?? 'lastDay';
  const { timeFrom, timeTo, pollInterval } = date.getDateBounds(
    timeRange,
    {
      from: routeParams.from,
      to: routeParams.to,
    },
    undefined,
    routeParams.fastModeRange,
  );
  const [fetchFirstN, setFetchFirstN] = useState(pageSize);
  const handleLoadMore = useCallback(() => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'operations_drilldown_load_more_click',
    });
    setFetchFirstN((previousValue) => previousValue + pageSize);
  }, [pageSize, trackCustomEvent]);
  const graphRef = useGraphRef();
  const isInBackground = useIsAppInBackgroundContext();
  const { data, loading } = useQuery<ErrorInsightsOpDrilldownQuery>(
    errorInsightsOpDrilldownQuery,
    {
      variables: {
        graphId: graphRef?.graphId,
        variant: graphRef?.graphVariant,
        first: fetchFirstN,
        filter: {
          ...buildErrorFiltersForListQuery(routeParams),
        },
        groupBy: [
          ErrorInsightsListGroupByColumn.OPERATION_ID,
          ErrorInsightsListGroupByColumn.OPERATION_NAME,
        ],
        from: timeFrom,
        to: timeTo,
        traceRefsLimit: defaultTraceRefsLimit,
      },
      skip: !graphRef,
      pollInterval,
      skipPollAttempt: useCallback(() => isInBackground, [isInBackground]),
    },
  );

  const nodes =
    data?.graph?.variant?.errorInsightsList.edges
      ?.map((e) => e.node)
      .filter(notEmpty) ?? [];
  const totalCount = data?.graph?.variant?.errorInsightsList.totalCount ?? 0;
  const location = useLocation();

  const handleBackClick = () => {
    trackCustomEvent({
      category: 'Operation Insights Errors',
      action: 'operations_drilldown_back_click',
    });
  };
  return loading ? (
    <ErrorBreakdownLoading />
  ) : (
    <>
      <Breadcrumb>
        <BreadcrumbItem>
          <BreadcrumbLink
            as={Link}
            to={insightsRouteConfig.locationFrom({
              location,
              patch: {
                errorsCodeFilter: RouteConfig.DELETE,
                errorsServiceFilter: RouteConfig.DELETE,
                errorsPathFilter: RouteConfig.DELETE,
                errorsDrilldown: RouteConfig.DELETE,
              },
            })}
            onClick={handleBackClick}
          >
            All errors
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <span>Operations</span>
          <Badge variant="neutralOutlined" className="ml-2">
            {totalCount}
          </Badge>
        </BreadcrumbItem>
      </Breadcrumb>
      <Card className="mt-6" variant="unstyled">
        <OperationsDrilldownView
          data={nodes.map((item) => ({
            operationId: item.operationId,
            operationName: item.operationName,
            errorCount: item.errorCount ?? 0,
            traceRefs: item.traceRefs,
          }))}
        />
        {totalCount > fetchFirstN ? (
          <CardFooter className="flex justify-center p-3">
            <span className="mr-2">
              <em>1 - {fetchFirstN}</em> of {totalCount}
            </span>
            <Button variant="buttonLink" onClick={handleLoadMore}>
              Load more
            </Button>
          </CardFooter>
        ) : null}
      </Card>
    </>
  );
}
